/**
 * @module Widget
 */

import { WidgetModel } from '@models/widget.model';
import { DescriptionsModel } from '@models/descriptions.model';
import { LabelsModel } from '@models/labels.model';
import { UidModel, UID } from '@models/uid.model';
import { IconModel } from '@models/icon.model';
import { ColorModel } from '@models/color.model';
import { LinksModel } from '@models/links.model';
import { LogsModel } from '@models/logs.model';
import { ModificationClass } from '@class/modification.class';
import { UidClass } from '@class/uid.class';
import { LabelsClass } from '@class/labels.class';
import { DescriptionsClass } from '@class/descriptions.class';
import { IconClass } from '@class/icon.class';
import { ColorClass } from '@class/color.class';
import { LinksClass } from '@class/links.class';
import { LogsClass } from '@class/logs.class';
import { ShareModel } from '@models/share.model';
import { ShareClass } from '@class/share.class';
import { TagsModel } from '@models/tag.model';
import { TagsClass } from '@class/tag.class';
import { DeepCopy } from '@functions/copy.functions';
import { RightsModel } from '@models/right.model';
import { RightsClass } from '@class/right.class';
import { FormControl, Validators } from '@angular/forms';

export class WidgetClass extends ModificationClass<WidgetModel> {
  
  private _uid: UidClass;
  private _labels: LabelsClass;
  private _descriptions: DescriptionsClass;
  private _icon: IconClass;
  private _color: ColorClass;
  private _links: LinksClass;
  private _logs: LogsClass;
  private _share: ShareClass;
  private _tags: TagsClass;
  private _rights: RightsClass;

  protected _parent: WidgetModel;
  protected _inital: WidgetModel;
  protected _attributes: string[];

  private _isRegistered: boolean;

  constructor(widget: WidgetModel, isRegistered: boolean = true) {

    super();

    this._parent = widget;
    this._isRegistered = isRegistered;

    this._form.addControl('uidTheme', new FormControl(this._parent.uidTheme, [ 
      Validators.required
    ]));

    this._reset(this._parent);

    this._attributes = this.attributes.slice();
    this._attributes.push(...this._labels.attributes);
    this._attributes.push(...this._descriptions.attributes);
    this._attributes.push(...this._uid.attributes);
    this._attributes.push(...this._icon.attributes);
    this._attributes.push(...this._color.attributes);
    this._attributes.push(...this._links.attributes);
    this._attributes.push(...this._logs.attributes);
    this._attributes.push(...this._share.attributes);
    this._attributes.push(...this._tags.attributes);
    this._attributes.push(...this._rights.attributes);

  }

  private _reset(widget: WidgetModel) {
    this._uid = new UidClass(widget as UidModel, this._isRegistered);
    this._labels = new LabelsClass(widget as LabelsModel, this._form, this._requirements, this._state);
    this._descriptions = new DescriptionsClass(widget as DescriptionsModel, this._form, this._requirements, this._state);
    this._icon = new IconClass(widget as IconModel, this._state);
    this._color = new ColorClass(widget as ColorModel, this._state);
    this._links = new LinksClass(widget as LinksModel, this._state);
    this._logs = new LogsClass(widget as LogsModel, this._state);
    this._share = new ShareClass(widget as ShareModel, this._state);
    this._tags = new TagsClass(widget as TagsModel, this._state);
    this._rights = new RightsClass(widget as RightsModel, this._state);

    this._form.setControl('uidTheme', new FormControl(this._parent.uidTheme, [ 
      Validators.required
    ]));

    this.attributes.forEach((attribute: string) => {
      this._parent[attribute] = widget[attribute];
    });

    this._inital = DeepCopy(widget);

    this._state.flush();
    this._modifications$.next(this._state);
  }

  reset() {
    this._state.flush();
    this._reset(this._inital);
  }

  get attributes(): Readonly<string[]> {
    return ['uidTheme'];
  }

  get class(): string {
    return 'widget';
  }

  get uidTheme(): UID {
    return this._parent.uidTheme;
  }

  get uid(): UidClass {
    return this._uid;
  }

  get labels(): LabelsClass {
    return this._labels;
  }

  get descriptions(): DescriptionsClass {
    return this._descriptions;
  }

  get icon(): IconClass {
    return this._icon;
  }

  get color(): ColorClass {
    return this._color;
  }

  get links(): LinksClass {
    return this._links;
  }

  get logs(): LogsClass {
    return this._logs;
  }

  get share(): ShareClass {
    return this._share;
  }

  get tags(): TagsClass {
    return this._tags;
  }

  get rights(): RightsClass {
    return this._rights;
  }

}